import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  Minus,
  UploadCloud,
  X
} from "lucide-react";
import type { FC } from "react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";

import { formatBytes } from "../documentsModel";

type FileStatus = "uploading" | "complete" | "failed" | "cancelled";

export type UploadFileItem = {
  key: string;
  name: string;
  sizeBytes: number;
  loadedBytes: number;
  totalBytes: number;
  percent: number;
  status: FileStatus;
};

type ToastProps = {
  files: UploadFileItem[];
  minimized: boolean;
  onToggleMinimized: () => void;
  onCancel: () => void;
};

const UploadProgressToast: FC<ToastProps> = ({
  files,
  minimized,
  onToggleMinimized,
  onCancel
}) => {
  const doneCount = files.filter((f) => f.status !== "uploading").length;
  const totalBytes = files.reduce((sum, f) => sum + (f.totalBytes || 0), 0);
  const isDone = doneCount === files.length;

  return (
    <div className="flex w-96 flex-col rounded-lg border border-border bg-popover text-popover-foreground shadow-lg">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div className="flex items-center gap-3">
          {isDone ? (
            <CheckCircle2 className="size-4 text-success" />
          ) : (
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          )}
          <div className="flex flex-col">
            <span className="text-sm font-semibold">
              {isDone
                ? "Upload complete"
                : `Uploading ${files.length} file${
                    files.length === 1 ? "" : "s"
                  }...`}
            </span>
            <span className="text-xs text-muted-foreground">
              {doneCount} of {files.length} complete
            </span>
          </div>
        </div>
        <div className="flex items-center gap-0.5">
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={minimized ? "Expand uploads" : "Minimize uploads"}
            onClick={(e) => {
              e.stopPropagation();
              onToggleMinimized();
            }}
          >
            <Minus />
          </Button>
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={isDone ? "Dismiss" : "Cancel uploads"}
            onClick={(e) => {
              e.stopPropagation();
              onCancel();
            }}
          >
            <X />
          </Button>
        </div>
      </div>

      {!minimized ? (
        <ul className="flex max-h-72 flex-col divide-y divide-border overflow-auto">
          {files.map((f) => {
            const complete = f.status === "complete";
            const failed = f.status === "failed" || f.status === "cancelled";
            const right = complete
              ? "Complete"
              : f.status === "cancelled"
              ? "Cancelled"
              : failed
              ? "Failed"
              : `${f.percent}%`;
            return (
              <li key={f.key} className="flex flex-col gap-1.5 px-4 py-3">
                <div className="flex items-center justify-between gap-2">
                  <span className="min-w-0 truncate text-sm font-medium">
                    {f.name}
                  </span>
                  {complete ? (
                    <CheckCircle2 className="size-4 shrink-0 text-success" />
                  ) : failed ? (
                    <AlertCircle className="size-4 shrink-0 text-destructive" />
                  ) : (
                    <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />
                  )}
                </div>
                <div className="flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">
                    {formatBytes(f.sizeBytes)}
                  </span>
                  <span
                    className={cn(
                      complete && "text-success",
                      failed && "text-destructive",
                      !complete && !failed && "font-semibold text-primary"
                    )}
                  >
                    {right}
                  </span>
                </div>
                <Progress
                  value={complete ? 100 : failed ? 100 : f.percent}
                  className={cn(
                    "h-1.5",
                    complete && "[&>[data-slot=progress-indicator]]:bg-success",
                    failed &&
                      "[&>[data-slot=progress-indicator]]:bg-destructive"
                  )}
                />
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="flex items-center justify-between rounded-b-lg border-t border-border bg-fill-1 px-4 py-2">
        <span className="text-xs text-muted-foreground">
          Total: {formatBytes(totalBytes)}
        </span>
        <Badge
          variant={isDone ? "secondary" : "default"}
          className="gap-1 text-xs"
        >
          {isDone ? (
            <CheckCircle2 className="size-3" />
          ) : (
            <UploadCloud className="size-3" />
          )}
          {isDone ? "Complete" : "Uploading"}
        </Badge>
      </div>
    </div>
  );
};

type UploadFn = (
  file: File,
  opts: {
    signal: AbortSignal;
    onProgress: (args: {
      percent: number;
      loaded: number;
      total: number;
    }) => void;
  }
) => Promise<unknown>;

/**
 * Uploads a batch of files while rendering a live progress toast (sonner
 * `toast.custom` re-rendered under a stable id). Resolves once every upload
 * has settled.
 */
export const runUploadBatch = async (
  files: File[],
  upload: UploadFn
): Promise<void> => {
  if (files.length === 0) return;

  const items: UploadFileItem[] = files.map((f, idx) => {
    const size = Number.isFinite(f.size) ? Math.max(0, f.size) : 0;
    return {
      key: `${Date.now()}_${idx}_${Math.random().toString(16).slice(2)}`,
      name: f.name || "Untitled",
      sizeBytes: size,
      loadedBytes: 0,
      totalBytes: size,
      percent: 0,
      status: "uploading"
    };
  });
  const controllers = files.map(() => new AbortController());
  const toastId = `fn-doc-upload:${items[0].key}`;
  let minimized = false;

  const render = (duration: number) => {
    toast.custom(
      () => (
        <UploadProgressToast
          files={[...items]}
          minimized={minimized}
          onToggleMinimized={() => {
            minimized = !minimized;
            render(60_000);
          }}
          onCancel={() => {
            controllers.forEach((c) => c.abort());
            items.forEach((it) => {
              if (it.status === "uploading") it.status = "cancelled";
            });
            toast.dismiss(toastId);
          }}
        />
      ),
      { id: toastId, duration, position: "bottom-right" }
    );
  };

  render(60_000);

  await Promise.allSettled(
    files.map(async (file, idx) => {
      const controller = controllers[idx];
      const item = items[idx];
      try {
        await upload(file, {
          signal: controller.signal,
          onProgress: ({ percent, loaded, total }) => {
            item.loadedBytes = loaded;
            item.totalBytes = total || item.totalBytes;
            item.percent = percent;
            render(60_000);
          }
        });
        item.status = "complete";
        item.percent = 100;
        item.loadedBytes = item.totalBytes || item.loadedBytes;
      } catch {
        item.status = controller.signal.aborted ? "cancelled" : "failed";
      } finally {
        render(60_000);
      }
    })
  );

  // Let the finished state linger briefly, then auto-dismiss.
  render(2500);
};
